import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, PieChart } from './_finHelpers';

export default function MarginCalc() {
  return (
    <CalcShell look="cnet" title="Margin Calculator" subtitle="Profit Margin Calculator + Stock Trading Margin Calculator."
      about={<><p>Compute profit margin and markup from cost/revenue, plus stock trading margin requirements.</p></>}
    >
      <ProfitMargin/>
      <hr className="my-8 border-slate-200"/>
      <StockMargin/>
      <hr className="my-8 border-slate-200"/>
      <CurrencyExchangeMargin/>
    </CalcShell>
  );
}

function CurrencyExchangeMargin() {
  const [interbank, setInterbank] = useState(1.0854);
  const [buyRate, setBuyRate] = useState(1.0762);
  const [sellRate, setSellRate] = useState(1.0946);
  const c = useMemo(() => {
    const buySpread = interbank > 0 ? ((interbank - buyRate) / interbank) * 100 : 0;
    const sellSpread = interbank > 0 ? ((sellRate - interbank) / interbank) * 100 : 0;
    const roundTrip = buySpread + sellSpread;
    return { buySpread, sellSpread, roundTrip };
  }, [interbank, buyRate, sellRate]);
  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900 mb-3">Currency Exchange Margin Calculator</h3>
      <p className="text-sm text-slate-600 mb-4">Compare a broker's currency buy/sell rate to the interbank (mid-market) rate to see the effective margin (spread) charged.</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Interbank / mid-market rate</label><input type="number" step="0.0001" value={interbank} onChange={e=>setInterbank(+e.target.value||0)} data-testid="fx-mid"/></div>
          <div className="field"><label>Broker buy rate (you sell to broker)</label><input type="number" step="0.0001" value={buyRate} onChange={e=>setBuyRate(+e.target.value||0)}/></div>
          <div className="field"><label>Broker sell rate (you buy from broker)</label><input type="number" step="0.0001" value={sellRate} onChange={e=>setSellRate(+e.target.value||0)}/></div>
        </div>
        <div>
          <h4 className="font-semibold text-slate-900 mb-2">Result</h4>
          <table className="data-table"><tbody>
            <tr><td>Buy-side margin (bank profit when you sell)</td><td className="text-right font-semibold" data-testid="fx-buy-margin">{c.buySpread.toFixed(3)}%</td></tr>
            <tr><td>Sell-side margin (bank profit when you buy)</td><td className="text-right font-semibold">{c.sellSpread.toFixed(3)}%</td></tr>
            <tr><td>Round-trip cost</td><td className="text-right font-semibold text-red-600">{c.roundTrip.toFixed(3)}%</td></tr>
          </tbody></table>
          <p className="text-xs text-slate-500 mt-2">Retail forex margins are typically 1–4%. Wire transfers often add another fixed fee on top.</p>
        </div>
      </div>
    </div>
  );
}

function ProfitMargin() {
  const [cost, setCost] = useState(120);
  const [revenue, setRevenue] = useState(160);
  const c = useMemo(() => {
    const profit = revenue - cost;
    const margin = revenue > 0 ? (profit/revenue)*100 : 0;
    const markup = cost > 0 ? (profit/cost)*100 : 0;
    return { profit, margin, markup };
  }, [cost, revenue]);
  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900 mb-3">Profit Margin Calculator</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Cost</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" step="0.01" value={cost} onChange={e=>setCost(+e.target.value||0)} className="flex-1"/></div><p className="text-xs text-slate-500 mt-1">The cost of the product.</p></div>
          <div className="field"><label>Revenue</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" step="0.01" value={revenue} onChange={e=>setRevenue(+e.target.value||0)} className="flex-1"/></div><p className="text-xs text-slate-500 mt-1">The income generated by selling the product.</p></div>
        </div>
        <div>
          <h4 className="font-semibold text-slate-900 mb-2">Result</h4>
          <table className="data-table"><tbody>
            <tr><td>Margin</td><td className="text-right font-semibold text-blue-700" data-testid="margin-pct">{c.margin.toFixed(2)}%</td></tr>
            <tr><td>Profit</td><td className="text-right">{fmt(c.profit)}</td></tr>
            <tr><td>Markup</td><td className="text-right">{c.markup.toFixed(2)}%</td></tr>
          </tbody></table>
          <div className="mt-4"><PieChart parts={[{label:'Cost', value:cost, color:'#2563eb'},{label:'Profit margin', value:c.profit, color:'#10b981'}]} size={170}/></div>
        </div>
      </div>
    </div>
  );
}

function StockMargin() {
  const [price, setPrice] = useState(18.3);
  const [shares, setShares] = useState(100);
  const [req, setReq] = useState(30);
  const c = useMemo(() => ({ amount: price * shares * req/100 }), [price, shares, req]);
  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900 mb-3">Stock Trading Margin Calculator</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Stock price</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" step="0.01" value={price} onChange={e=>setPrice(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Number of shares</label><input type="number" value={shares} onChange={e=>setShares(+e.target.value||0)}/></div>
          <div className="field"><label>Margin requirement</label><div className="flex gap-2"><input type="number" step="0.01" value={req} onChange={e=>setReq(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
        </div>
        <div>
          <div className="result-panel"><div className="result-title">Amount required</div><div className="result-value" data-testid="stock-margin">{fmt(c.amount)}</div></div>
        </div>
      </div>
    </div>
  );
}
